import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import numberUtils from 'donor-elf-web/lib/number-utils';
import ContactModel from 'donor-elf-web/models/contact';
import GeocodeModel from 'donor-elf-web/models/geocode';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import { Changeset } from 'ember-changeset';
import FileSaver from 'file-saver';
import Papa from 'papaparse';
import { getDistance } from 'geolib';

export default class MapMyContactsController extends Controller {
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare store: Store;

  queryParams = ['address', 'miles', 'group'];

  @tracked address = '';
  @tracked miles = '50';
  @tracked group = '';

  @tracked addressGeocode?: GeocodeModel;

  @tracked filterExpanded = false;
  @tracked filterChangeset?: any;
  @tracked currentMapBounds?: { lat: number[]; lng: number[] };
  @tracked loading = false;

  @action
  templateDidInsert() {
    this.load();
  }

  @action
  templateWillDestroy() {
    this.clearFilter();
    this.filterChangeset = undefined;
    this.filterExpanded = false;
    this.loading = false;
  }

  @action
  async load() {
    this.loading = true;
    this.addressGeocode = undefined;

    this.filterChangeset = Changeset({
      address: this.address,
      miles: this.miles,
      group: this.group,
    });

    if (isPresent(this.address)) {
      let geocode = (
        await this.store.query('geocode', { filter: { address: this.address } })
      ).slice()[0];
      this.addressGeocode = geocode;
    }

    this.loading = false;
  }

  @cached
  get mappedContacts() {
    let mappedContacts = this.confidentLocationContacts.slice();
    if (this.addressGeocode) {
      let range = numberUtils.milesToMeters(+this.miles);
      let fromCoords = {
        latitude: this.addressGeocode.latitude,
        longitude: this.addressGeocode.longitude,
      };
      mappedContacts = mappedContacts.filter((contact) => {
        let contactCoords = {
          latitude: contact.latitude,
          longitude: contact.longitude,
        };
        return getDistance(fromCoords, contactCoords) < range;
      });
    }
    if (isPresent(this.group)) {
      mappedContacts = mappedContacts.filter((contact) => {
        return contact.groups?.some((x) => x === this.group);
      });
    }
    return mappedContacts;
  }

  @cached
  get mappedContactsInBounds() {
    let mappedContacts = this.mappedContacts;
    if (this.currentMapBounds) {
      return mappedContacts.filter((contact) => {
        return (
          contact.latitude! >= this.currentMapBounds!.lat[0]! &&
          contact.latitude! <= this.currentMapBounds!.lat[1]! &&
          contact.longitude! >= this.currentMapBounds!.lng[0]! &&
          contact.longitude! <= this.currentMapBounds!.lng[1]!
        );
      });
    } else {
      return mappedContacts;
    }
  }

  mileOptions = [
    {
      label: '25 miles',
      val: '25',
    },
    {
      label: '50 miles',
      val: '50',
    },
    {
      label: '75 miles',
      val: '75',
    },
    {
      label: '100 miles',
      val: '100',
    },
    {
      label: '200 miles',
      val: '200',
    },
    {
      label: '300 miles',
      val: '300',
    },
    {
      label: '400 miles',
      val: '400',
    },
    {
      label: '500 miles',
      val: '500',
    },
  ];

  get groupOptions() {
    return [
      {
        val: '',
        label: 'Any',
      },
    ].concat(
      this.contactsCache.sortedGroups.map((x: string) => {
        return { val: x, label: x };
      }),
    );
  }

  get filtering() {
    return isPresent(this.address) || isPresent(this.group);
  }

  get filterMessage() {
    if (this.filtering) {
      let messages = [];
      if (isPresent(this.address)) {
        messages.push(`living within ${this.miles} miles of '${this.address}'`);
      }
      if (isPresent(this.group)) {
        messages.push(`in the '${this.group}' group`);
      }
      return `Contacts ${messages.join(' and ')}`;
    } else {
      return 'All contacts';
    }
  }

  get actionsMenuItems() {
    return [
      {
        label: this.filterExpanded ? 'Collapse Filter' : 'Adjust Filter',
        svgJar: 'filter',
        onClick: () => {
          this.toggleExpanded();
        },
      },
      {
        label: 'Download',
        svgJar: 'download',
        onClick: () => {
          this.download();
        },
      },
    ];
  }

  @cached
  get confidentLocationContacts() {
    console.log('confidentLocationContacts', this.contactsCache.sortedContacts.filter((contact) => {
      return contact.city === 'Denver' && (contact.locationConfidence || 0) >= 0.5;
    }));

    return this.contactsCache.sortedContacts.filter((contact) => {
      return (contact.locationConfidence || 0) >= 0.5;
    });
  }

  @cached
  get notConfidentLocationContacts() {
    let confidentIds: any = {};
    this.confidentLocationContacts.forEach((contact) => {
      confidentIds[contact.id] = true;
    });
    return this.contactsCache.sortedContacts.filter((contact) => {
      return !confidentIds[contact.id];
    });
  }

  get mappedContactsWithPinNumber() {
    return this.mappedContacts.map((contact: ContactModel, index: number) => {
      return {
        pinNumber: index + 1,
        contact,
      };
    });
  }

  get hasMappedContacts() {
    return isPresent(this.mappedContacts);
  }

  get confidentLocationContactItems() {
    return this.mappedContacts.map((contact) => {
      return {
        contact,
        location: [contact.latitude, contact.longitude],
      };
    });
  }

  get mapBounds() {
    let minLat = 90;
    let maxLat = -90;
    let minLong = 180;
    let maxLong = -180;
    this.mappedContacts.forEach((contact) => {
      if (contact.latitude! < minLat) {
        minLat = contact.latitude!;
      }
      if (contact.latitude! > maxLat) {
        maxLat = contact.latitude!;
      }
      if (contact.longitude! < minLong) {
        minLong = contact.longitude!;
      }
      if (contact.longitude! > maxLong) {
        maxLong = contact.longitude!;
      }
    });

    let bounds = [
      [minLat, minLong],
      [maxLat, maxLong],
    ];

    if (bounds[0]!.join('-') == bounds[1]!.join('-')) {
      bounds = [
        [minLat - 0.005, minLong + 0.005],
        [maxLat + 0.005, maxLong + 0.005],
      ];
    }
    return bounds;
  }

  fitBoundsOptions = {
    padding: [5, 5],
  };

  @action
  handleLeafletMoveEnd(e: any) {
    let currentBounds = e.target.getBounds();
    this.currentMapBounds = {
      lat: [currentBounds._southWest.lat, currentBounds._northEast.lat],
      lng: [currentBounds._southWest.lng, currentBounds._northEast.lng],
    };
  }

  handleLeafletMapInserted(el: any) {
    let distanceFromTop = el.getBoundingClientRect().top;
    let elementHeight = window.innerHeight - distanceFromTop - 150;
    if (elementHeight > 750) {
      elementHeight = 750;
    } else if (elementHeight < 350) {
      elementHeight = 350;
    }
    el.style.height = `${elementHeight}px`;
  }

  @action
  applyFilter() {
    if (this.filterChangeset.get('isPristine')) {
      alert('No filter values have changed');
      return;
    }
    this.address = this.filterChangeset.get('address');
    this.miles = this.filterChangeset.get('miles');
    this.group = this.filterChangeset.get('group');
    this.load();
  }

  @action
  clearFilter() {
    this.address = '';
    this.miles = '50';
    this.group = '';
    this.load();
  }

  @action
  toggleExpanded() {
    this.filterExpanded = !this.filterExpanded;
  }

  @action
  async download() {
    let contacts = this.mappedContactsInBounds;
    let rows = await this.contactsExport.perform(contacts);

    this.store.createRecord('contact-download').save();

    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'mapped-contacts.csv', true);
  }
}
